import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import kyumoLogo from '../assets/kyumo.png';
import nodejsLogo from '../assets/nodejs.png';
import rancherDesktopLogo from '../assets/rancher-desktop.png';
import uvLogo from '../assets/uv.png';
import { useWebAuth } from '../contexts/WebAuthContext';
import { getVersionInfoService, VersionInfoService } from '../services/VersionInfoService';

/**
 * ダウンロードページ
 *
 * 認証済みユーザー向けにアプリケーションのダウンロードを提供
 */
export function DownloadPage() {
  const { user, signOut } = useWebAuth();
  const {
    data: versionInfo,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['versionInfo'],
    queryFn: () => getVersionInfoService().getLatestVersion(),
  });
  const error = queryError ? 'バージョン情報の取得に失敗しました' : null;

  const detectedPlatform = VersionInfoService.detectPlatform();

  const handleDownload = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ヘッダー */}
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={kyumoLogo} alt="Kyumo" className="w-8 h-8" />
            <h1 className="text-xl font-bold">きゅーもAI エージェント v2</h1>
            {import.meta.env.VITE_APP_ENVIRONMENT &&
              import.meta.env.VITE_APP_ENVIRONMENT !== 'prod' && (
                <span className="px-1 py-px text-[9px] font-medium rounded bg-orange-500 text-white">
                  {import.meta.env.VITE_APP_ENVIRONMENT.toUpperCase()}
                </span>
              )}
          </div>
          <div className="flex items-center gap-4">
            {user?.groups?.some((g) => g === 'admin' || g === 'audit') && (
              <Link
                to="/admin"
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                管理画面
              </Link>
            )}
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Link
              to="/account/security"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              認証アプリの登録
            </Link>
            <button
              type="button"
              onClick={signOut}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ログアウト
            </button>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* バージョン情報 */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">アプリケーションをダウンロード</h2>
          {loading ? (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <p className="text-red-400">{error}</p>
          ) : (
            <p className="text-muted-foreground">
              最新バージョン:{' '}
              <span className="text-foreground font-semibold">{versionInfo?.version}</span>
            </p>
          )}
        </div>

        {/* ダウンロードボタン */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Windows */}
          <div className="bg-card/50 border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg
                className="w-8 h-8 text-blue-400"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                aria-label="Windows"
              >
                <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold">Windows</h3>
                <p className="text-sm text-muted-foreground">Windows 11以降</p>
              </div>
            </div>

            {versionInfo?.windows ? (
              <>
                <button
                  type="button"
                  onClick={() => handleDownload(versionInfo.windows?.installer.url ?? '')}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    detectedPlatform === 'windows'
                      ? 'bg-primary-500 hover:bg-primary-600 text-white'
                      : 'bg-muted hover:bg-accent text-foreground'
                  }`}
                >
                  ダウンロード (.exe)
                </button>
                <p className="mt-2 text-xs text-muted-foreground text-center">
                  {VersionInfoService.formatFileSize(versionInfo.windows.installer.size)}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">現在利用できません</p>
            )}
          </div>

          {/* macOS */}
          <div className="bg-card/50 border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg
                className="w-8 h-8 text-foreground"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                aria-label="macOS"
              >
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold">macOS</h3>
                <p className="text-sm text-muted-foreground">macOS 13以降</p>
              </div>
            </div>

            {versionInfo?.macos ? (
              <>
                <button
                  type="button"
                  onClick={() => handleDownload(versionInfo.macos?.installer.url ?? '')}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    detectedPlatform === 'macos'
                      ? 'bg-primary-500 hover:bg-primary-600 text-white'
                      : 'bg-muted hover:bg-accent text-foreground'
                  }`}
                >
                  ダウンロード (.dmg)
                </button>
                <p className="mt-2 text-xs text-muted-foreground text-center">
                  {VersionInfoService.formatFileSize(versionInfo.macos.installer.size)}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">現在利用できません</p>
            )}
          </div>
        </div>

        {/* システム要件 */}
        <div className="bg-card/30 border border-border/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">システム要件</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-foreground mb-2">Windows</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Windows 11 (64bit) 以降</li>
                <li>8GB以上のRAM</li>
                <li>2GB以上の空きディスク容量</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">macOS</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>macOS 13 (Ventura) 以降</li>
                <li>Intel/Apple Silicon対応</li>
                <li>8GB以上のRAM</li>
                <li>2GB以上の空きディスク容量</li>
              </ul>
            </div>
          </div>
        </div>

        {/* インストール手順 */}
        <div className="mt-8 bg-card/30 border border-border/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">インストール手順</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-foreground mb-2">Windows</h4>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>上のボタンからインストーラー(.exe)をダウンロード</li>
                <li>ダウンロードしたファイルを実行</li>
                <li>
                  SmartScreen警告が表示された場合:
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>「詳細情報」をクリック</li>
                    <li>「実行」ボタンをクリック</li>
                  </ul>
                </li>
                <li>画面の指示に従ってインストール</li>
                <li>インストール完了後、アプリを起動</li>
              </ol>
              <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600 dark:text-yellow-400">
                現在アプリは署名されていないため、SmartScreen警告が表示されます
              </div>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">macOS</h4>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>上のボタンからディスクイメージ(.dmg)をダウンロード</li>
                <li>ダウンロードしたファイルを開く</li>
                <li>アプリをApplicationsフォルダにドラッグ</li>
                <li>
                  ターミナルで以下のコマンドを実行
                  <code className="block mt-1 px-2 py-1 bg-muted rounded text-xs text-foreground font-mono">
                    xattr -cr /Applications/Kyumo\ AI\ Agent\ v2.app
                  </code>
                </li>
                <li>Applicationsからアプリを起動</li>
              </ol>
              <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600 dark:text-yellow-400">
                現在アプリは署名されていないため、手順4のコマンド実行が必要です
              </div>
            </div>
          </div>
        </div>

        {/* 関連ソフトウェア */}
        <div className="mt-8 bg-card/30 border border-border/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-2">関連ソフトウェア（オプション）</h3>
          <p className="text-sm text-muted-foreground mb-6">
            利用する機能に応じてインストールしてください
          </p>

          {/* コンテナランタイム */}
          <h4 className="font-medium text-foreground mb-3">
            コンテナランタイム（サンドボックス機能）
          </h4>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <a
              href="https://rancherdesktop.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-card/50 border border-border rounded-lg hover:border-primary-500/50 transition-colors"
            >
              <img
                src={rancherDesktopLogo}
                alt="Rancher Desktop"
                className="w-8 h-8 shrink-0 rounded"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">Rancher Desktop</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-600 dark:text-green-400">
                  推奨
                </span>
                <span className="text-muted-foreground text-xs">
                  ※ コンテナエンジンは moby を選択してください
                </span>
              </div>
            </a>
            <a
              href="https://www.docker.com/products/docker-desktop/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-card/50 border border-border rounded-lg hover:border-primary-500/50 transition-colors"
            >
              <svg
                className="w-8 h-8 shrink-0 text-blue-400"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                aria-label="Docker Desktop"
              >
                <path d="M13.983 11.078h2.119a.186.186 0 0 0 .186-.185V9.006a.186.186 0 0 0-.186-.186h-2.119a.186.186 0 0 0-.187.186v1.887c0 .102.084.185.187.185zm-2.954-5.43h2.118a.186.186 0 0 0 .187-.185V3.575a.186.186 0 0 0-.187-.186h-2.118a.186.186 0 0 0-.187.186v1.888c0 .102.084.185.187.185zm0 2.716h2.118a.187.187 0 0 0 .187-.186V6.29a.187.187 0 0 0-.187-.186h-2.118a.187.187 0 0 0-.187.186v1.887c0 .103.084.186.187.186zm-2.93 0h2.12a.186.186 0 0 0 .186-.186V6.29a.186.186 0 0 0-.186-.186h-2.12a.186.186 0 0 0-.184.186v1.887c0 .103.083.186.185.186zm-2.964 0h2.119a.186.186 0 0 0 .185-.186V6.29a.186.186 0 0 0-.185-.186H5.136a.186.186 0 0 0-.186.186v1.887c0 .103.084.186.186.186zm5.893 2.715h2.118a.186.186 0 0 0 .187-.185V9.006a.186.186 0 0 0-.187-.186h-2.118a.186.186 0 0 0-.187.186v1.887c0 .102.084.185.187.185zm-2.93 0h2.12a.186.186 0 0 0 .186-.185V9.006a.186.186 0 0 0-.186-.186h-2.12a.186.186 0 0 0-.184.186v1.887c0 .102.083.185.185.185zm-2.964 0h2.119a.186.186 0 0 0 .185-.185V9.006a.186.186 0 0 0-.185-.186H5.136a.186.186 0 0 0-.186.186v1.887c0 .102.084.185.186.185zm-2.92 0h2.12a.185.185 0 0 0 .184-.185V9.006a.185.185 0 0 0-.184-.186h-2.12a.185.185 0 0 0-.184.186v1.887c0 .102.082.185.185.185zM23.763 9.89c-.065-.051-.672-.51-1.954-.51-.338 0-.676.03-1.01.087-.248-1.7-1.653-2.53-1.716-2.566l-.344-.199-.226.327c-.284.438-.49.922-.612 1.43-.23.97-.09 1.882.403 2.661-.595.332-1.55.413-1.744.42H.751a.751.751 0 0 0-.75.748 11.687 11.687 0 0 0 .692 4.062c.545 1.428 1.355 2.48 2.41 3.124 1.18.72 3.13 1.132 5.332 1.132.983 0 1.978-.09 2.96-.272a11.44 11.44 0 0 0 3.328-1.27 10.09 10.09 0 0 0 2.53-2.1c1.28-1.517 2.04-3.22 2.608-4.762h.226c1.4 0 2.26-.569 2.736-1.048a2.86 2.86 0 0 0 .69-1.025l.096-.264z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">Docker Desktop</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="text-muted-foreground text-xs">
                  ※ 有償ライセンスが必要な場合があります
                </span>
              </div>
            </a>
          </div>

          {/* Node.js / uv */}
          <h4 className="font-medium text-foreground mb-3">
            Node.js / uv（ローカル MCP サーバー）
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="https://nodejs.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-card/50 border border-border rounded-lg hover:border-primary-500/50 transition-colors"
            >
              <img src={nodejsLogo} alt="Node.js" className="w-8 h-8 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">Node.js</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="text-muted-foreground text-xs">JavaScript ランタイム</span>
              </div>
            </a>
            <a
              href="https://docs.astral.sh/uv/getting-started/installation/#installation-methods"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-card/50 border border-border rounded-lg hover:border-primary-500/50 transition-colors"
            >
              <img src={uvLogo} alt="uv" className="w-8 h-8 shrink-0 p-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">uv</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="text-muted-foreground text-xs">Python パッケージマネージャー</span>
              </div>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
